// src/pages/purchase-requisition/PengajuanBarang.jsx
import React, { useState, useMemo } from "react";
import {
    Typography, Box, Breadcrumbs, Card, CardContent,
    CircularProgress, Alert, Snackbar,
    IconButton, Stack, Link as MuiLink,
    ClickAwayListener, Tooltip,
    TextField, InputAdornment
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from '@mui/icons-material/Search';
import { useOutletContext, useNavigate } from "react-router-dom";
import useAuthCheck from "../../hooks/useAuthCheck";
import CustomBreadcrumbs from '../../components/universal-features/breadcrumbs';
import * as XLSX from "xlsx";

// Import custom hooks
import useOrderData from "./hooks/useOrderData";
import useOrderForm from "./hooks/useOrderForm";
import useOrderApproval from "./hooks/useOrderApproval";
import useUserTooltipData from "./hooks/useUserTooltipData";
import useOrderSearch from "./hooks/useOrderSearch";

// Import components
import OrderTable from "./components/OrderTable";
import OrderFormDialog from "./components/OrderFormDialog";
import OrderDetailDialog from "./components/OrderDetailDialog";
import ApproveConfirmDialog from "./components/ApproveConfirmDialog";
import RejectConfirmDialog from "./components/RejectConfirmDialog";

// New component for User Tooltip - moved here to be exported for OrderTable
export const UserTooltipDisplay = ({ uid, userName }) => {
    // Each instance of UserTooltipDisplay now uses its own useUserTooltipData hook
    // This import is here because it's used by this component, not PengajuanBarang directly
    const {
        tooltipOpen,
        tooltipContent,
        tooltipAnchorEl,
        handleTooltipOpen,
        handleTooltipClose
    } = useUserTooltipData();

    return (
        <ClickAwayListener onClickAway={handleTooltipClose}>
            <Tooltip
                open={tooltipOpen}
                onClose={handleTooltipClose}
                anchorEl={tooltipAnchorEl}
                title={
                    <Box sx={{ p: 1 }}>
                        <Typography variant="body2"><strong>Nama:</strong> {tooltipContent.name}</Typography>
                        <Typography variant="body2"><strong>Departemen:</strong> {tooltipContent.department}</Typography>
                        <Typography variant="body2"><strong>Jabatan:</strong> {tooltipContent.jabatan}</Typography>
                        <Typography variant="body2"><strong>Role:</strong> {tooltipContent.roles.join(', ')}</Typography>
                    </Box>
                }
            >
                <span
                    onMouseEnter={(e) => handleTooltipOpen(e, uid)}
                    onMouseLeave={handleTooltipClose}
                    style={{ cursor: 'pointer' }}
                >
                    {userName}
                </span>
            </Tooltip>
        </ClickAwayListener>
    );
};


function PengajuanBarang() {
    const { user, loading: authLoading, userProfile } = useAuthCheck();
    const navigate = useNavigate();
    const { breadcrumbs } = useOutletContext();

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    // Use custom hook for order data
    const {
        orders,
        loadingOrders,
        flowConfigurations,
        loadingFlows,
        adminJabatanId,
        loadingAdminJabatanId,
        uomList,
        loadingUom,
        prcnInvMaterialPackPHRoleId,
        invManagementFreshPineOtherRoleId,
        loadingRoles,
    } = useOrderData(user, userProfile);

    // Use custom hook for order search
    const {
        searchTerm,
        setSearchTerm,
        filteredOrders,
    } = useOrderSearch(orders); // Pass the raw orders to the search hook

    // Use custom hook for order form
    const {
        formValues,
        setFormValues, // TAMBAHKAN INI
        openFormDialog,
        handleOpenFormDialog,
        handleCloseFormDialog,
        openItemDetailDialog,
        handleOpenItemDetailDialog,
        handleCloseItemDetailDialog,
        currentItemDetail,
        currentItemDetailIndex,
        handleItemDetailChange,
        handleSaveItemDetail,
        handleDeleteItemDetail,
        handleSubmitOrder,
        orderBeingEditedId,
        handleResubmitOrder,
        // HAPUS handleMainFormChange dari sini
    } = useOrderForm(user, userProfile, flowConfigurations, setSnackbar);

    // Use custom hook for order approval
    const {
        openApproveConfirmDialog,
        handleCloseApproveConfirmDialog,
        handleConfirmApprove,
        openRejectConfirmDialog,
        handleCloseRejectConfirmDialog,
        handleConfirmReject,
        rejectionNote,
        setRejectionNote,
        handleOpenConfirmDialog,
        //orderToConfirm,
    } = useOrderApproval(user, userProfile, orders, flowConfigurations, setSnackbar);

    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const handleViewOrder = (order) => {
        setSelectedOrder(order);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedOrder(null);
    };

    // Logic to determine if "Ajukan Permintaan Baru" button should be visible
    const canCreateNewOrder = useMemo(() => {
        if (!user || !userProfile || !userProfile.id_jabatan || loadingAdminJabatanId || adminJabatanId === null) {
            return false;
        }
        const isUserAdmin = userProfile.id_jabatan === adminJabatanId;
        return isUserAdmin;
    }, [user, userProfile, adminJabatanId, loadingAdminJabatanId]);

    // Determine if flow configuration should be read-only
    const isFlowConfigReadOnly = !!userProfile?.id_flow_order_default;

    // Helper function to get UoM name from ID (added here)
    const getUomName = (uomId) => {
        if (!Array.isArray(uomList)) {
            return uomId;
        }
        const uom = uomList.find(item => item.id === uomId);
        return uom ? uom.nama_uom : uomId;
    };

    // Function to download material item data to Excel
    const handleDownloadExcel = (order) => {
        if (!order || !order.item_details || order.item_details.length === 0) {
            setSnackbar({ open: true, message: "Tidak ada data item material untuk diunduh.", severity: "warning" });
            return;
        }

        const ws_data = [];
        const header = [
            "Kode Material",
            "Deskripsi Material",
            "QTY",
            "UoM",
            "Valuation Price",
            "Jumlah Total",
            "Cost Center",
            "Noted Kebutuhan",
        ];
        ws_data.push(header);

        const ws = XLSX.utils.aoa_to_sheet(ws_data); // Create empty sheet with header row

        // Add data rows with cell formatting
        order.item_details.forEach((item, index) => {
            const row = [
                item.material_code,
                item.material_description,
                item.quantity_jul,
                getUomName(item.unit_of_measure),
                parseFloat(String(item.valuation_price).replace(/[^0-9]/g, '')), // Store as number for formatting
                parseFloat(String(item.total_amount).replace(/[^0-9]/g, '')), // Store as number for formatting
                item.cost_center,
                item.notes,
            ];
            XLSX.utils.sheet_add_aoa(ws, [row], { origin: -1 }); // Append row

            // Apply number formatting based on data type and UoM
            const rowIndex = index + 1; // 0-indexed for data, 1-indexed for Excel rows
            const qtyCell = XLSX.utils.encode_cell({ r: rowIndex, c: 2 }); // Column C (QTY)
            const valuationPriceCell = XLSX.utils.encode_cell({ r: rowIndex, c: 4 }); // Column E (Harga Valuasi)
            const totalAmountCell = XLSX.utils.encode_cell({ r: rowIndex, c: 5 }); // Column F (Jumlah Total)

            // QTY formatting
            const uomName = getUomName(item.unit_of_measure);
            if (typeof item.quantity_jul === 'number' && !isNaN(item.quantity_jul)) {
                // Check if UoM is a common non-numeric unit, otherwise assume number
                if (uomName.toLowerCase() === 'pcs' || uomName.toLowerCase() === 'unit' || uomName.toLowerCase() === 'buah') {
                    if (ws[qtyCell]) ws[qtyCell].t = 's'; // Set type to string if UoM is non-numeric
                } else {
                    if (ws[qtyCell]) ws[qtyCell].t = 'n'; // Set type to number
                    if (ws[qtyCell]) ws[qtyCell].z = '#,##0'; // Number format without decimals
                }
            } else {
                if (ws[qtyCell]) ws[qtyCell].t = 's'; // Default to string if not a valid number
            }


            // Valuation Price and Total Amount formatting (always number with thousand separators)
            if (ws[valuationPriceCell]) {
                ws[valuationPriceCell].t = 'n'; // Set type to number
                ws[valuationPriceCell].z = '#,##0'; // Number format without decimals
            }
            if (ws[totalAmountCell]) {
                ws[totalAmountCell].t = 'n'; // Set type to number
                ws[totalAmountCell].z = '#,##0'; // Number format without decimals
            }
        });


        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Detail Item Material");

        // Create file name
        const fileName = `Order_${order.id}_Item_Details.xlsx`;

        // Download file
        XLSX.writeFile(wb, fileName);
        setSnackbar({ open: true, message: "Data item material berhasil diunduh!", severity: "success" });
    };

// Fungsi untuk menghitung interval kumulatif (dari pengajuan sampai step tertentu)
const calculateCumulativeInterval = (order) => {
    if (!order || !order.created_at) {
        return "-";
    }

    try {
        let endTime;
        
        if (order.status === "Approved") {
            // Untuk approved, hitung sampai approval terakhir
            if (order.approval_history && order.approval_history.length > 0) {
                const approvedEntries = order.approval_history.filter(entry => 
                    entry.decision === "Approved"
                );
                
                if (approvedEntries.length > 0) {
                    approvedEntries.sort((a, b) => 
                        new Date(b.timestamp) - new Date(a.timestamp)
                    );
                    const finalApproval = approvedEntries[0];
                    endTime = new Date(finalApproval.timestamp);
                }
            }
            
            if (!endTime && order.updated_at) {
                endTime = new Date(order.updated_at);
            }
        } else if (order.status === "Rejected") {
            // Untuk rejected, hitung sampai rejection
            if (order.approval_history && order.approval_history.length > 0) {
                const rejectedEntry = order.approval_history.find(entry => 
                    entry.decision === "Rejected"
                );
                if (rejectedEntry && rejectedEntry.timestamp) {
                    endTime = new Date(rejectedEntry.timestamp);
                }
            }
            
            if (!endTime && order.updated_at) {
                endTime = new Date(order.updated_at);
            }
        } else {
            // Untuk pending, hitung sampai step terakhir yang di-approve
            if (order.approval_history && order.approval_history.length > 0) {
                const approvedEntries = order.approval_history.filter(entry => 
                    entry.decision === "Approved"
                );
                
                if (approvedEntries.length > 0) {
                    // Ambil approval terakhir
                    approvedEntries.sort((a, b) => 
                        new Date(b.timestamp) - new Date(a.timestamp)
                    );
                    const latestApproval = approvedEntries[0];
                    endTime = new Date(latestApproval.timestamp);
                }
            }
        }
        
        // Jika tidak ada approval_history, gunakan waktu sekarang untuk pending
        if (!endTime) {
            endTime = new Date();
        }
        
        const startTime = new Date(order.created_at);
        const diffMs = endTime - startTime;
        
        return formatInterval(diffMs);
        
    } catch (error) {
        console.error("Error calculating cumulative interval:", error);
        return "-";
    }
};

// Helper function untuk format interval (sama seperti di HistoryPengajuan)
const formatInterval = (diffMs) => {
    if (diffMs <= 0) return "0 detik";
    
    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const remainingSeconds = totalSeconds % 86400;
    const hours = Math.floor(remainingSeconds / 3600);
    const remainingMinutesSeconds = remainingSeconds % 3600;
    const minutes = Math.floor(remainingMinutesSeconds / 60);
    const seconds = remainingMinutesSeconds % 60;
    
    if (days > 0) {
        if (hours > 0) {
            return `${days} hari ${hours} jam ${minutes} menit ${seconds} detik`;
        }
        return `${days} hari ${minutes} menit ${seconds} detik`;
    } else {
        if (hours > 0) {
            return `${hours} jam ${minutes} menit ${seconds} detik`;
        }
        if (minutes > 0) {
            return `${minutes} menit ${seconds} detik`;
        }
        return `${seconds} detik`;
    }
};


    if (authLoading || loadingOrders || loadingFlows || loadingAdminJabatanId || loadingUom || loadingRoles) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 0 }}>
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Card>
                <CardContent sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="h6" component="h2">
                            Daftar Pengajuan
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {/* Search Input */}
                            <TextField
                                variant="outlined"
                                size="small"
                                placeholder="Cari order..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ width: { xs: '100%', sm: 'auto' }, mr: 1 }}
                            />
                            {canCreateNewOrder && (
                                <IconButton
                                    color="primary"
                                    size="small"
                                    onClick={() => handleOpenFormDialog()}>
                                        <AddIcon />
                                </IconButton>
                            )}
                        </Stack>
                    </Stack>

                    <OrderTable
                        orders={filteredOrders}
                        flowConfigurations={flowConfigurations}
                        user={user}
                        userProfile={userProfile}
                        prcnInvMaterialPackPHRoleId={prcnInvMaterialPackPHRoleId}
                        invManagementFreshPineOtherRoleId={invManagementFreshPineOtherRoleId}
                        handleViewOrder={handleViewOrder}
                        handleOpenConfirmDialog={handleOpenConfirmDialog}
                        handleResubmitOrder={handleResubmitOrder}
                        handleDownloadExcel={handleDownloadExcel}
                        getUomName={getUomName}
                        calculateCumulativeInterval={calculateCumulativeInterval}
                    />
                </CardContent>
            </Card>

            {/* Render OrderFormDialog component */}
            <OrderFormDialog
                open={openFormDialog}
                onClose={handleCloseFormDialog}
                formValues={formValues}
                // HAPUS handleMainFormChange={handleMainFormChange}
                flowConfigurations={flowConfigurations}
                handleOpenItemDetailDialog={handleOpenItemDetailDialog}
                handleDeleteItemDetail={handleDeleteItemDetail}
                handleSubmitOrder={handleSubmitOrder}
                openItemDetailDialog={openItemDetailDialog}
                handleCloseItemDetailDialog={handleCloseItemDetailDialog}
                currentItemDetail={currentItemDetail}
                currentItemDetailIndex={currentItemDetailIndex}
                handleItemDetailChange={handleItemDetailChange}
                handleSaveItemDetail={handleSaveItemDetail}
                setSnackbar={setSnackbar}
                isFlowConfigReadOnly={isFlowConfigReadOnly}
                uomList={uomList}
                orderBeingEditedId={orderBeingEditedId}
                setFormValues={setFormValues} // TAMBAHKAN INI
            />

            {/* Render OrderDetailDialog component */}
            <OrderDetailDialog
                open={openViewDialog}
                onClose={handleCloseViewDialog}
                selectedOrder={selectedOrder}
                flowConfigurations={flowConfigurations}
                uomList={uomList}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Approved Confirmation Dialog */}
            <ApproveConfirmDialog
                open={openApproveConfirmDialog}
                onClose={handleCloseApproveConfirmDialog}
                onConfirm={handleConfirmApprove}
            />

            {/* Rejected Confirmation Dialog */}
            <RejectConfirmDialog
                open={openRejectConfirmDialog}
                onClose={handleCloseRejectConfirmDialog}
                onConfirm={handleConfirmReject}
                rejectionNote={rejectionNote}
                setRejectionNote={setRejectionNote}
            />

        </Box>
    );
}

export default PengajuanBarang;